import { useLocation } from "@remix-run/react";

/**
 * Devuelve siempre la misma URL para `/x` y para `/x.md`: agregar la extensión
 * a ciegas duplicaba el sufijo cuando el pathname ya la traía.
 */
function toMarkdownUrl(siteUrl: string, pathname: string) {
  const base = pathname.endsWith(".md") ? pathname.slice(0, -".md".length) : pathname;
  return `${siteUrl}${base}.md`;
}

/**
 * Per-page auto-discovery tags for the LLM SEO Framework (element 7).
 *
 * Emits the `<link rel="alternate" type="text/markdown">` + the two `ai-*`
 * meta tags ONLY for pages that have a Markdown alternate served by
 * apps/web/server.js (course/news/article detail pages). On listing,
 * transactional, or auth pages it renders nothing.
 *
 * The allowlist must stay in sync with the routes registered in server.js:
 *   /courses/:slug         → /courses/:slug.md
 *   /news/:id              → /news/:id.md
 *   /resources/:id         → /resources/:id.md
 *
 * `/courses/category/:slug` is intentionally excluded — category pages are
 * listing pages, not detail pages, and don't have a `.md` twin.
 *
 * OJO: mientras server.js no tenga los handlers `.md`, la propia URL `/x.md`
 * cae al catch-all de Remix y renderiza este componente. Por eso el href se
 * arma de forma idempotente (ver `toMarkdownUrl`): sin eso salía `.md.md`.
 *
 * See:
 *   .agents/skills/llm-seo-framework/references/auto-discovery-tags.md
 *   .agents/skills/llm-seo-framework/references/academia-upgrade-tasks.md (Task 6)
 */
export function MarkdownAlternateTags({ siteUrl }: { siteUrl: string }) {
  const location = useLocation();
  const path = location.pathname;

  const hasMarkdownAlternate =
    /^\/courses\/(?!category\/)[^/]+$/.test(path) ||
    /^\/news\/[^/]+$/.test(path) ||
    /^\/resources\/[^/]+$/.test(path);

  if (!hasMarkdownAlternate) return null;

  const mdUrl = toMarkdownUrl(siteUrl, path);

  return (
    <>
      <link rel="alternate" type="text/markdown" href={mdUrl} title="LLM-friendly version" />
      <meta name="ai-content-available" content="markdown, structured-data" />
      <meta name="ai-markdown-url" content={mdUrl} />
    </>
  );
}
